/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { hostedEventsApi } from "./api";
import { FormDatePicker, FormTimePicker } from "../events/dateTimePickers";
import { LocationSection, locFromEvent, locationPayload, validateLocation } from "../events/location";
import "./HostedEvents.css";

export default function HostedEventForm() {
  const { id } = useParams();      // undefined = create
  const isEdit = !!id;
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [loc, setLoc] = useState(() => locFromEvent(null));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");

  const patchLoc = (patch) => setLoc((l) => ({ ...l, ...patch }));

  useEffect(() => {
    if (!isEdit) return;
    hostedEventsApi.get(id).then((ev) => {
      setTitle(ev.title || "");
      setDescription(ev.description || "");
      setEventDate(ev.event_date || "");
      setStartTime(ev.start_time || "");
      setEndTime(ev.end_time || "");
      setLoc(locFromEvent(ev));
      setLoading(false);
    }).catch((e) => {
      setError(e.message || "Could not load event");
      setLoading(false);
    });
  }, [id, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required"); return; }
    if (!eventDate) { setError("Event date is required"); return; }
    if (startTime && endTime && endTime <= startTime) {
      setError("End time must be after start time");
      return;
    }
    const locError = validateLocation(loc);
    if (locError) { setError(locError); return; }
    setSaving(true);
    setError("");
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        event_date: eventDate,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        ...locationPayload(loc),
      };
      if (isEdit) {
        await hostedEventsApi.patch(id, body);
        navigate(`/hosted-events/${id}`);
      } else {
        const ev = await hostedEventsApi.create(body);
        navigate(`/hosted-events/${ev.id}`);
      }
    } catch (e) {
      setError(e.message || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="he-loading">Loading…</div>;

  return (
    <div className="he-page he-form-page">
      <div className="he-back-bar">
        <button className="he-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
      </div>
      <h1 className="he-heading">{isEdit ? "Edit event" : "Host an event"}</h1>
      {!isEdit && (
        <p className="he-form-hint">
          Create the event, then invite neighbours and add an invitation card.
          Invitations can be sent once the event is at least 3 days away.
        </p>
      )}
      {error && <p className="he-error">{error}</p>}

      <form className="he-form" onSubmit={submit}>
        <label className="he-label">
          Title <span className="he-req">*</span>
          <input className="he-input" value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Diwali get-together" />
        </label>

        <div className="he-row">
          <div className="he-label he-third">
            <span>Date <span className="he-req">*</span></span>
            <FormDatePicker className="he-input" value={eventDate} onChange={setEventDate} />
          </div>
          <div className="he-label he-third">
            <span>Start time</span>
            <FormTimePicker className="he-input" value={startTime} onChange={setStartTime} />
          </div>
          <div className="he-label he-third">
            <span>End time</span>
            <FormTimePicker className="he-input" value={endTime} onChange={setEndTime} />
          </div>
        </div>

        <div className="he-label">
          <span>Location</span>
          <LocationSection loc={loc} onChange={patchLoc} inputClass="he-input" />
        </div>

        <label className="he-label">
          Description
          <textarea className="he-textarea" value={description}
            onChange={(e) => setDescription(e.target.value)} rows={4}
            placeholder="What's the occasion? Dress code, what to bring, etc." />
        </label>

        <button className="he-submit" type="submit" disabled={saving}>
          <Save size={16} /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create event"}
        </button>
      </form>
    </div>
  );
}
